import { toast } from "@iventis/toasts/src/toast";
import { Content } from "@iventis/translations";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { UseIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FeatureCollection } from "geojson";
import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { SystemDataFieldName } from "@iventis/domain-model/model/systemDataFieldName";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { ValidationError, ValidationResponse } from "./layer-geojson-validation-types";
import { MAX_SIZE_MB } from "./layer-geojson-validation-constants";

/** Shows the error toast depending on the validation error */
export function showValidationErrorToast(validationError: ValidationResponse, translate: UseIventisTranslate, mapLayer: MapLayer) {
    switch (validationError.error) {
        case ValidationError.InvalidJson:
        case ValidationError.InvalidGeoJson:
        case ValidationError.InvalidGPX:
        case ValidationError.InvalidKML:
            toast.warning(
                {
                    title: translate(Content.map7.importGeoJson.invalidGeoJson.title),
                    message: translate(Content.map7.importGeoJson.invalidGeoJson.message),
                    testId: "error-invalid-file",
                },
                {
                    persist: true,
                }
            );
            break;
        case ValidationError.TooLarge:
            toast.warning(
                {
                    title: translate(Content.map7.importGeoJson.fileTooLarge.title),
                    message: translate(Content.map7.importGeoJson.fileTooLarge.message, { size: `${MAX_SIZE_MB}mb` }),
                },
                {
                    persist: true,
                }
            );
            break;
        case ValidationError.InvalidCoordinates:
        case ValidationError.GeometryTypeNotHandled:
            toast.warning(
                {
                    title: translate(Content.map7.importGeoJson.invalidGeometry.title),
                    message: translate(Content.map7.importGeoJson.invalidGeometry.message),
                    testId: "error-invalid-geometry",
                },
                {
                    persist: true,
                }
            );
            break;
        case ValidationError.InvalidParsedKML:
            toast.warning(
                {
                    title: translate(Content.map7.importKML.invalidKML.title),
                    message: translate(Content.map7.importKML.invalidKML.message),
                    testId: "error-invalid-KML-GeoJson",
                },
                {
                    persist: true,
                }
            );
            break;
        case ValidationError.InvalidParsedGPX:
            toast.warning(
                {
                    title: translate(Content.map7.importGpx.invalidGpx.title),
                    message: translate(Content.map7.importGpx.invalidGpx.message),
                    testId: "error-invalid-GPX-GeoJson",
                },
                {
                    persist: true,
                }
            );
            break;
        case ValidationError.InvalidLayerType:
            toast.warning(
                {
                    title: translate(Content.map7.importGeoJson.invalidLayerType.title),
                    message: translate(Content.map7.importGeoJson.invalidLayerType.message, {
                        importType: validationError.importFeatureType,
                        layerType: mapLayer.styleType.toString(),
                    }),
                    testId: "error-invalid-layer-type",
                },
                {
                    persist: true,
                }
            );
            break;
        case ValidationError.InvalidCoordinateSystem:
            toast.warning(
                {
                    title: translate(Content.map7.importGeoJson.invalidCoordinateSystem.title),
                    message: translate(Content.map7.importGeoJson.invalidCoordinateSystem.message, { coordinateSystem: "EPSG: 4326" }),
                },
                {
                    persist: true,
                }
            );
            break;
        default:
            toast.warning(
                {
                    title: translate(Content.map7.importGeoJson.somethingWentWrong.title),
                    message: translate(Content.map7.importGeoJson.somethingWentWrong.message),
                },
                {
                    persist: true,
                }
            );
            break;
    }
}

/** Takes an array of features and gets all unique property value */
export const getFeaturesProperties = (featureCollection: FeatureCollection) => {
    const features = featureCollection?.features;

    if (features == null) {
        throw new Error("Features can't be null");
    }

    const allProperties = features.reduce((properties, feature) => {
        // Get all property keys for the feature
        const propertyKeys = Object.keys(feature?.properties ?? {});
        const newProperties = [];
        propertyKeys.forEach((property) => {
            // If property does not already exist then add the array
            if (!properties.includes(property)) {
                newProperties.push(property);
            }
        });
        return [...properties, ...newProperties];
    }, []);

    return allProperties;
};

/**
 * Filter out the system attributes which are calculated when map objects are being imported
 * image attributes
 */
export const getMappableAttributes = (attributes: DataField[]) =>
    attributes.filter(
        (attribute) =>
            attribute.systemDataFieldName !== SystemDataFieldName.MapObjectArea &&
            attribute.systemDataFieldName !== SystemDataFieldName.MapObjectLength &&
            attribute.type !== DataFieldType.Image &&
            attribute.systemDataFieldName !== SystemDataFieldName.QuantityOfModelsOnLine
    );

/** For each attribute get the list and create a map with them */
export async function getListItemsForLayer(
    attributes: DataField[],
    getListItemForAttribute: (attributeId: string) => Promise<DataFieldListItem[]>
): Promise<{ [attributeId: string]: DataFieldListItem[] }> {
    const requests = attributes.map((attribute) => getListItemForAttribute(attribute.id));
    const response = await Promise.all(requests);
    const attributeListItemMap = {};
    attributes.forEach(({ id }, index) => {
        attributeListItemMap[id] = response?.[index] ?? [];
    });
    return attributeListItemMap;
}
